import {
  Box,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import CategoriesField from "components/elements/CategoriesField";
import DocumentsUpload from "components/elements/DocumentsUpload";
import LoansField from "components/elements/LoansField";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useEffect, useState } from "react";
import { uploadDocument } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { SelectOption } from "utils/types";
import { getCustomButtonVariant } from "utils/uiconfighelper";

const UploadDocuments = ({ defaultLoan }: { defaultLoan?: SelectOption | null }) => {
  const { user } = useTenant();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [files, setFiles] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [loanOption, setLoanOption] = useState<SelectOption | null>(defaultLoan ?? null);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState<SelectOption | null>(null);

  const checkEditUploadPermission = () => {
    if (hasPermission(user, permissionConstants.editAndUpload)) {
      return false;
    }
    if (!isPermissionEnabled(user)) {
      // no permissions
      return false;
    }
    return true;
  };

  const onClick = async () => {
    try {
      if (!loanOption) {
        toast({
          title: "Select Loan",
          description: "Please select a loan.",
          status: "error",
        });
        return;
      }
      setUploading(true);

      if (!files[0]) {
        return toast({
          title: "Upload File",
          description: "Please upload a file.",
          status: "error",
        });
      }

      const uploadPromises = files.map(file =>
        uploadDocument({
          loan_id: loanOption?.value,
          file: file,
          name,
          category: category?.value,
        })
      );

      await Promise.all(uploadPromises);
      await queryClient.invalidateQueries({
        queryKey: ["documents-list"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["documents-for-loan-list"],
      });
      onClose();
    } catch (err) {
      toast({
        title: "Error Occured",
        description: getErrorMessage(err),
        status: "error",
      });
    } finally {
      setUploading(false);
      setLoanOption(null);
      setName("");
      setFiles([]);
    }
  };

  useEffect(() => {
    setCategory(null);
  }, [loanOption]);

  return (
    <Box>
      <Button
        isDisabled={checkEditUploadPermission()}
        onClick={onOpen}
        variant={getCustomButtonVariant(user)}
      >
        Upload Document
      </Button>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent minW="555px">
          <ModalHeader borderBottomColor="#E2E2E2" borderBottomWidth="1px">
            Upload Document
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p="28px">
            {!defaultLoan && (
              <LoansField loan={loanOption} setLoan={setLoanOption} isRequired={true} />
            )}
            <CategoriesField
              category={category}
              setCategory={setCategory}
              loan_id={loanOption?.value}
            />
            <Box>
              <DocumentsUpload maxFiles={100} files={files} setFiles={setFiles} />
            </Box>
          </ModalBody>

          <ModalFooter justifyContent={"end"}>
            <Button
              variant="secondary"
              onClick={onClose}
              isLoading={uploading}
              isDisabled={uploading}
            >
              Cancel
            </Button>
            <Button
              variant={getCustomButtonVariant(user)}
              ml={3}
              onClick={onClick}
              isLoading={uploading}
              isDisabled={uploading || !loanOption || files.length === 0}
            >
              Upload {files.length > 1 ? files.length : ""} Document
              {files.length > 1 ? "s" : ""}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UploadDocuments;
